import { useCallback, useEffect, useMemo, useState, FormEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { pagesApi } from "../api/pagesApi";
import { graphApi } from "../api/graphApi";
import { ForceGraph3DWrapper } from "../graph3d/ForceGraph3DWrapper";
import { DetailDrawer } from "../components/DetailDrawer";
import type { ArchitectureOverview, ADR, CreateADRPayload, UpdateADRPayload, InfrastructureNode } from "../types/pages";
import type { GraphData, GraphNode, GraphEdge } from "../types/graph";
import type { FilterState } from "../types/filters";
import CustomButton from "../components/CustomButton";
import { themeColor } from "../utils";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

type Tab = "overview" | "infrastructure" | "cross-service" | "adrs";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "cross-service", label: "Cross-service" },
  { id: "adrs", label: "ADRs" },
];

function isValidArchTab(s: string | null): s is Tab {
  return TABS.some((t) => t.id === s);
}

export function ArchitecturePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState<Tab>(isValidArchTab(urlTab) ? urlTab : "overview");

  // Sync tab when navigated to with ?tab= from another page (e.g. route links → cross-service)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (isValidArchTab(t)) setTab(t);
  }, [searchParams]);

  function switchTab(t: Tab) {
    setTab(t);
    setSearchParams({ tab: t }, { replace: true });
  }

  return (
    <div className="content">
      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`tab-btn${tab === t.id ? " active" : ""}`}
            onClick={() => switchTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "overview" && <OverviewTab />}
      {tab === "infrastructure" && <InfraTab />}
      {tab === "cross-service" && <CrossServiceTab />}
      {tab === "adrs" && <ADRsTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab (original ArchitecturePage content)
// ---------------------------------------------------------------------------

function OverviewTab() {
  const { projectId = "", repoId = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ArchitectureOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    pagesApi.getArchitecture(repoId, projectId)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repoId, projectId]);

  if (loading) return <div className="card"><p className="muted">Loading architecture overview…</p></div>;
  if (error) return <div className="card"><p className="pill danger">{error}</p></div>;
  if (!data) return <div className="card"><h3 style={{ marginTop: 0 }}>Architecture Overview</h3><p className="muted" data-testid="empty-state">Repository not yet indexed.</p></div>;

  const isEmpty = data.languages.length === 0 && data.packages.length === 0 && data.entry_points.length === 0 && data.routes.length === 0 && data.hotspots.length === 0;
  if (isEmpty) return <div className="card"><h3 style={{ marginTop: 0 }}>Architecture Overview</h3><p className="muted" data-testid="empty-state">Repository not yet indexed. Run an indexing job to generate architecture data.</p></div>;

  return (
    <div className="card">
      {data.languages.length > 0 && (
        <section data-testid="section-languages">
          <h3>Languages</h3>
          <ul>{data.languages.map((l) => <li key={l.language}>{l.language} — {l.file_count} file{l.file_count !== 1 ? "s" : ""}</li>)}</ul>
        </section>
      )}
      {data.packages.length > 0 && (
        <section data-testid="section-packages">
          <h3>Packages</h3>
          <ul>{data.packages.map((p) => <li key={p.path}>{p.name}</li>)}</ul>
        </section>
      )}
      {data.entry_points.length > 0 && (
        <section data-testid="section-entry-points">
          <h3>Entry Points</h3>
          <ul>{data.entry_points.map((ep) => <li key={ep.id}><strong>{ep.name}</strong> <span className="muted">{ep.file_path}</span></li>)}</ul>
        </section>
      )}
      {data.routes.length > 0 && (
        <section data-testid="section-routes">
          <h3>Routes</h3>
          <ul>
            {data.routes.map((route) => (
              <li key={`${route.method}-${route.path}`}>
                <a href="#" data-testid="route-link" onClick={(e) => { e.preventDefault(); navigate(`/projects/${projectId}/repos/${repoId}/architecture?tab=cross-service`); }}>
                  <span className="pill">{route.method}</span> {route.path}
                </a>{" "}
                <span className="muted">→ {route.handler}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {data.hotspots.slice(0, 10).length > 0 && (
        <section data-testid="section-hotspots">
          <h3>Hotspots (Top 10)</h3>
          <ul>
            {data.hotspots.slice(0, 10).map((hs) => (
              <li key={hs.id}>
                <a href="#" data-testid="hotspot-link" onClick={(e) => { e.preventDefault(); navigate(`/projects/${projectId}/repos/${repoId}/explore?focus=${hs.fq_name}`); }}>{hs.name}</a>{" "}
                <span className="muted">fan-in: {hs.fan_in}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {data.service_boundaries.length > 0 && (
        <section data-testid="section-service-boundaries">
          <h3>Service Boundaries</h3>
          <ul>{data.service_boundaries.map((sb) => <li key={sb.boundary}>{sb.boundary} — {sb.module_count} module{sb.module_count !== 1 ? "s" : ""} <span className="muted">(cohesion: {(sb.cohesion * 100).toFixed(0)}%)</span></li>)}</ul>
        </section>
      )}
      {data.layers.length > 0 && (
        <section data-testid="section-layers">
          <h3>Layers</h3>
          <ul>{data.layers.map((l) => <li key={l.name}>{l.name} — {l.module_count} module{l.module_count !== 1 ? "s" : ""}</li>)}</ul>
        </section>
      )}
      {data.communities.length > 0 && (
        <section data-testid="section-communities">
          <h3>Communities</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {[...data.communities].sort((a, b) => a.community_id - b.community_id).map((c) => (
              <div key={c.community_id} className="card" style={{ padding: 14, margin: 0, width: "fit-content" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontWeight: 600 }}>Community {c.community_id}</span>
                  <span className="pill">{c.size} node{c.size !== 1 ? "s" : ""}</span>
                </div>
                {c.sample_modules.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {c.sample_modules.map((mod) => (
                      <span key={mod} className="muted" style={{ fontSize: 12, fontFamily: "monospace", color: themeColor, padding: "2px 6px", borderRadius: 4 }}>
                        {mod}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Infrastructure tab
// ---------------------------------------------------------------------------

function isInfraNode(node: GraphNode): node is InfrastructureNode {
  return "resource_kind" in node;
}

function infraShape(resourceKind: string): string {
  switch (resourceKind) {
    case "Dockerfile": return "cube";
    case "Deployment": return "cylinder";
    case "Service": return "cone";
    default: return "octahedron";
  }
}

function InfraTab() {
  const { projectId = "", repoId = "" } = useParams();
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    graphApi.getInfrastructureGraph(repoId, projectId)
      .then((data) => { if (!cancelled) setGraphData(data); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repoId, projectId]);

  if (loading) return <div className="card"><p className="muted">Loading...</p></div>;
  if (error) return <div className="card"><div className="pill danger">{error}</div></div>;
  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Infrastructure</h3>
        <p className="muted">No IaC resources detected. Dockerfiles, Kubernetes Deployments, and Services will appear here once detected.</p>
      </div>
    );
  }

  const infraEdges = graphData.edges.filter((e) => e.type === "DEPLOYS" || e.type === "OVERLAYS");

  return (
    <div className="card">
      <p className="muted">
        {graphData.nodes.length} infrastructure resource{graphData.nodes.length !== 1 ? "s" : ""} · {infraEdges.length} relationship{infraEdges.length !== 1 ? "s" : ""}
      </p>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="projects-th">Name</TableCell>
              <TableCell className="projects-th">Kind</TableCell>
              <TableCell className="projects-th">Shape</TableCell>
              <TableCell className="projects-th">File</TableCell>
              <TableCell className="projects-th">Namespace</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {graphData.nodes.map((node) => {
              const infra = isInfraNode(node) ? node : null;
              const resourceKind = infra?.resource_kind ?? "Other";
              return (
                <TableRow key={node.id} className="projects-row" onClick={() => setSelectedNode(node)} style={{ cursor: "pointer" }} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedNode(node); } }}>
                  <TableCell className="projects-td" sx={{ fontFamily: "monospace" }}>{node.name}</TableCell>
                  <TableCell className="projects-td"><span className="pill">{resourceKind}</span></TableCell>
                  <TableCell className="projects-td">{infraShape(resourceKind)}</TableCell>
                  <TableCell className="projects-td" sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(120,120,125,1) !important" }}>{node.file_path}</TableCell>
                  <TableCell className="projects-td">{infra?.namespace ?? "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {infraEdges.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4>Relationships</h4>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="projects-th">Source</TableCell>
                  <TableCell className="projects-th">Type</TableCell>
                  <TableCell className="projects-th">Target</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {infraEdges.map((edge) => {
                  const src = graphData.nodes.find((n) => n.id === edge.source);
                  const tgt = graphData.nodes.find((n) => n.id === edge.target);
                  return (
                    <TableRow key={edge.id} className="projects-row">
                      <TableCell className="projects-td" sx={{ fontFamily: "monospace" }}>{src?.name ?? edge.source}</TableCell>
                      <TableCell className="projects-td"><span className="pill">{edge.type}</span></TableCell>
                      <TableCell className="projects-td" sx={{ fontFamily: "monospace" }}>{tgt?.name ?? edge.target}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      <DetailDrawer node={selectedNode} onClose={() => setSelectedNode(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cross-service tab
// ---------------------------------------------------------------------------

const CROSS_SERVICE_EDGE_TYPES = new Set(["HTTP_CALLS", "CROSS_CALLS", "EMITS", "LISTENS_ON"]);

interface RouteEntry { nodeId: string; method: string; path: string; handler: string; }

function extractRoutes(nodes: GraphNode[]): RouteEntry[] {
  return nodes.filter((n) => n.kind === "Route").map((n) => {
    const m = n.name.match(/^(GET|POST|PUT|DELETE|PATCH|HEAD|OPTIONS)\s+(.+)$/i);
    return { nodeId: n.id, method: m ? m[1].toUpperCase() : "GET", path: m ? m[2] : n.name, handler: n.fq_name || n.name };
  });
}

function filterCrossServiceGraph(data: GraphData): GraphData {
  const crossEdges = data.edges.filter((e) => CROSS_SERVICE_EDGE_TYPES.has(e.type));
  const ids = new Set<string>();
  for (const edge of crossEdges) { ids.add(edge.source); ids.add(edge.target); }
  return { nodes: data.nodes.filter((n) => ids.has(n.id)), edges: crossEdges };
}

function buildEdgeTooltip(edge: GraphEdge, nodes: GraphNode[]): string {
  const src = nodes.find((n) => n.id === edge.source)?.name ?? edge.source;
  const tgt = nodes.find((n) => n.id === edge.target)?.name ?? edge.target;
  let tip = `${edge.type}: ${src} → ${tgt}`;
  if (edge.confidence != null) tip += ` (confidence: ${Math.round(edge.confidence * 100)}%)`;
  return tip;
}

function CrossServiceTab() {
  const { projectId = "", repoId = "" } = useParams();
  const [rawData, setRawData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<GraphEdge | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    graphApi.getCrossServiceGraph(repoId, projectId)
      .then((result) => { if (!cancelled) setRawData(result); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repoId, projectId]);

  const graphData = useMemo<GraphData>(() => rawData ? filterCrossServiceGraph(rawData) : { nodes: [], edges: [] }, [rawData]);
  const routes = useMemo<RouteEntry[]>(() => extractRoutes(graphData.nodes), [graphData.nodes]);
  const filters = useMemo<FilterState>(() => ({
    languages: [], kinds: [], communityIds: [], edgeTypes: Array.from(CROSS_SERVICE_EDGE_TYPES), crossServiceOnly: true,
  }), []);
  const edgeTooltip = useMemo(() => hoveredEdge ? buildEdgeTooltip(hoveredEdge, graphData.nodes) : null, [hoveredEdge, graphData.nodes]);

  const handleNodeClick = useCallback((node: GraphNode) => setSelectedNodeId(node.id), []);

  if (loading) return <div className="card"><p className="muted">Loading cross-service graph…</p></div>;
  if (error) return <div className="card"><p className="pill danger">{error}</p></div>;
  if (!rawData || graphData.nodes.length === 0) {
    return <div className="card"><h3 style={{ marginTop: 0 }}>Cross-service</h3><p className="muted" data-testid="empty-state">No cross-service connections detected in this repository.</p></div>;
  }

  return (
    <div style={{ display: "flex", height: "calc(100vh - 180px)" }}>
      <aside data-testid="routes-panel" style={{ width: 280, minWidth: 280, overflowY: "auto", borderRight: "1px solid #30363d", padding: 16, backgroundColor: "#0d1117" }}>
        <h3 style={{ margin: "0 0 12px", color: "#e6edf3" }}>Routes</h3>
        {routes.length === 0 ? (
          <p className="muted" data-testid="no-routes">No routes detected.</p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {routes.map((route) => (
              <li key={route.nodeId} data-testid="route-item" onClick={() => setSelectedNodeId(route.nodeId)}
                style={{ padding: "8px 12px", marginBottom: 4, borderRadius: 6, cursor: "pointer", backgroundColor: selectedNodeId === route.nodeId ? "#1f6feb33" : "transparent", border: `1px solid ${selectedNodeId === route.nodeId ? "#1f6feb" : "transparent"}` }}>
                <span data-testid="route-method" style={{ display: "inline-block", padding: "2px 6px", borderRadius: 4, fontSize: 11, fontWeight: 600, marginRight: 8, backgroundColor: "#21262d", color: "#58a6ff" }}>{route.method}</span>
                <span data-testid="route-path" style={{ color: "#e6edf3" }}>{route.path}</span>
                <div data-testid="route-handler" style={{ fontSize: 12, color: "#7d8590", marginTop: 4 }}>{route.handler}</div>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div style={{ flex: 1, position: "relative", minHeight: 400 }}>
        <ForceGraph3DWrapper
          graphData={graphData}
          filters={filters}
          selectedNodeId={selectedNodeId}
          searchHighlights={new Set<string>()}
          colorMode="kind"
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={() => {}}
          onBackgroundClick={() => { setSelectedNodeId(null); setHoveredEdge(null); }}
          onEdgeClick={setHoveredEdge}
        />
        {edgeTooltip && (
          <div data-testid="edge-tooltip" style={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", padding: "6px 12px", borderRadius: 6, backgroundColor: "#161b22", border: "1px solid #30363d", color: "#e6edf3", fontSize: 13, pointerEvents: "none", zIndex: 10 }}>
            {edgeTooltip}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ADRs tab (full CRUD from ADRPage)
// ---------------------------------------------------------------------------

type ADRStatus = "proposed" | "accepted" | "deprecated" | "superseded";
type ADRView = "list" | "detail" | "create" | "edit";

const STATUS_OPTIONS: ADRStatus[] = ["proposed", "accepted", "deprecated", "superseded"];
const TITLE_MAX = 200;
const CONTENT_MAX = 50_000;

export function validateADRTitle(title: string): boolean { return title.length >= 1 && title.length <= TITLE_MAX; }
export function validateADRContent(content: string): boolean { return content.length >= 1 && content.length <= CONTENT_MAX; }

function ADRsTab() {
  const { projectId = "", repoId = "" } = useParams();
  const navigate = useNavigate();

  const [adrs, setAdrs] = useState<ADR[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ADRView>("list");
  const [selectedAdr, setSelectedAdr] = useState<ADR | null>(null);

  // Create form state
  const [createTitle, setCreateTitle] = useState("");
  const [createStatus, setCreateStatus] = useState<ADRStatus>("proposed");
  const [createDate, setCreateDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [createContent, setCreateContent] = useState("");
  const [createLinkedSymbols, setCreateLinkedSymbols] = useState("");
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Edit form state
  const [editStatus, setEditStatus] = useState<ADRStatus>("proposed");
  const [editContent, setEditContent] = useState("");
  const [editLinkedSymbols, setEditLinkedSymbols] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    pagesApi.getAdrs(repoId, projectId)
      .then((data) => { if (!cancelled) setAdrs(data); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repoId, projectId]);

  const versionHistory = useMemo(() => {
    if (!selectedAdr) return [];
    const adrMap = new Map(adrs.map((a) => [a.id, a]));
    const chain: ADR[] = [];
    let current: ADR | undefined = selectedAdr;
    const visited = new Set<string>();
    while (current?.supersedes && !visited.has(current.supersedes)) {
      visited.add(current.supersedes);
      const prev = adrMap.get(current.supersedes);
      if (prev) chain.unshift(prev);
      current = prev;
    }
    chain.push(selectedAdr);
    current = selectedAdr;
    visited.clear();
    while (current?.superseded_by && !visited.has(current.superseded_by)) {
      visited.add(current.superseded_by);
      const next = adrMap.get(current.superseded_by);
      if (next) chain.push(next);
      current = next;
    }
    return chain;
  }, [selectedAdr, adrs]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    if (!validateADRTitle(createTitle)) { setCreateError(`Title must be 1–${TITLE_MAX} characters.`); return; }
    if (!validateADRContent(createContent)) { setCreateError(`Content must be 1–${CONTENT_MAX} characters.`); return; }
    const linkedIds = createLinkedSymbols.split(",").map((s) => s.trim()).filter(Boolean);
    const payload: CreateADRPayload = { title: createTitle, status: createStatus, content: createContent, linked_symbols: linkedIds };
    setCreating(true);
    try {
      const newAdr = await pagesApi.createAdr(repoId, payload, projectId);
      setAdrs((prev) => [...prev, newAdr]);
      setView("list");
    } catch (err) { setCreateError((err as Error).message); }
    finally { setCreating(false); }
  }

  function handleEditSubmit(e: FormEvent) {
    e.preventDefault();
    setEditError(null);
    if (!validateADRContent(editContent)) { setEditError(`Content must be 1–${CONTENT_MAX} characters.`); return; }
    setShowConfirmDialog(true);
  }

  async function handleConfirmUpdate() {
    if (!selectedAdr) return;
    setShowConfirmDialog(false);
    const linkedIds = editLinkedSymbols.split(",").map((s) => s.trim()).filter(Boolean);
    const payload: UpdateADRPayload = { status: editStatus, content: editContent, linked_symbols: linkedIds };
    setEditing(true);
    try {
      const updated = await pagesApi.updateAdr(repoId, selectedAdr.id, payload, projectId);
      setAdrs((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setSelectedAdr(updated);
      setView("detail");
    } catch (err) { setEditError((err as Error).message); }
    finally { setEditing(false); }
  }

  function openDetail(adr: ADR) {
    setSelectedAdr(adr);
    setView("detail");
    // Fetch full detail to get content + linked_symbols (list endpoint returns summaries only)
    setLoadingDetail(true);
    pagesApi.getAdr(repoId, adr.id, projectId)
      .then((full) => { setSelectedAdr(full); setAdrs((prev) => prev.map((a) => (a.id === full.id ? full : a))); })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }

  if (loading) return <div className="card"><p className="muted">Loading...</p></div>;
  if (error) return <div className="card"><div className="pill danger">{error}</div></div>;

  if (view === "create") {
    return (
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Create New ADR</h3>
        <form onSubmit={handleCreate}>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="adr-title">Title (max {TITLE_MAX} chars)</label>
            <input id="adr-title" type="text" value={createTitle} onChange={(e) => setCreateTitle(e.target.value)} maxLength={TITLE_MAX} style={{ width: "100%", marginTop: 4 }} aria-label="ADR title" />
            <span className="muted" style={{ fontSize: 12 }}>{createTitle.length}/{TITLE_MAX}</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="adr-status">Status</label>
            <select id="adr-status" value={createStatus} onChange={(e) => setCreateStatus(e.target.value as ADRStatus)} style={{ width: "100%", marginTop: 4 }}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="adr-date">Date</label>
            <input id="adr-date" type="date" value={createDate} onChange={(e) => setCreateDate(e.target.value)} style={{ width: "100%", marginTop: 4 }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="adr-content">Content</label>
            <textarea id="adr-content" value={createContent} onChange={(e) => setCreateContent(e.target.value)} maxLength={CONTENT_MAX} rows={10} style={{ width: "100%", marginTop: 4 }} />
            <span className="muted" style={{ fontSize: 12 }}>{createContent.length}/{CONTENT_MAX}</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="adr-symbols">Linked Symbols (comma-separated IDs)</label>
            <input id="adr-symbols" type="text" value={createLinkedSymbols} onChange={(e) => setCreateLinkedSymbols(e.target.value)} style={{ width: "100%", marginTop: 4 }} placeholder="sym-1, sym-2, ..." />
          </div>
          {createError && <div style={{ marginBottom: 12 }}><span className="pill danger">{createError}</span></div>}
          <div className="row">
            <CustomButton loading={creating} buttonText="Create ADR" buttonTextLoading="Creating..." handleSubmitClick={() => {}} />
            <CustomButton loading={false} buttonText="Cancel" buttonTextLoading="" handleSubmitClick={() => setView("list")} background="transparent" color="rgba(74, 30, 71, 1)" border="1px solid rgba(203, 46, 186, 1)" hoverBgColor="rgba(203, 46, 186, 0.08)" />
          </div>
        </form>
      </div>
    );
  }

  if (view === "edit" && selectedAdr) {
    return (
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Edit ADR: {selectedAdr.title}</h3>
        <form onSubmit={handleEditSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="edit-status">Status</label>
            <select id="edit-status" value={editStatus} onChange={(e) => setEditStatus(e.target.value as ADRStatus)} style={{ width: "100%", marginTop: 4 }}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="edit-content">Content</label>
            <textarea id="edit-content" value={editContent} onChange={(e) => setEditContent(e.target.value)} maxLength={CONTENT_MAX} rows={10} style={{ width: "100%", marginTop: 4 }} />
            <span className="muted" style={{ fontSize: 12 }}>{editContent.length}/{CONTENT_MAX}</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="edit-symbols">Linked Symbols (comma-separated IDs)</label>
            <input id="edit-symbols" type="text" value={editLinkedSymbols} onChange={(e) => setEditLinkedSymbols(e.target.value)} style={{ width: "100%", marginTop: 4 }} />
          </div>
          {editError && <div style={{ marginBottom: 12 }}><span className="pill danger">{editError}</span></div>}
          <div className="row">
            <CustomButton loading={editing} buttonText="Save Changes" buttonTextLoading="Saving..." handleSubmitClick={() => {}} />
            <CustomButton loading={false} buttonText="Cancel" buttonTextLoading="" handleSubmitClick={() => setView("detail")} background="transparent" color="rgba(74, 30, 71, 1)" border="1px solid rgba(203, 46, 186, 1)" hoverBgColor="rgba(203, 46, 186, 0.08)" />
          </div>
        </form>
        {showConfirmDialog && (
          <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm update" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div className="card" style={{ maxWidth: 480, width: "100%", padding: 24 }}>
              <h4 style={{ marginTop: 0 }}>Confirm Update</h4>
              <p>Updating this ADR will create a new version with a SUPERSEDES link. This cannot be undone.</p>
              <div className="row" style={{ justifyContent: "flex-end" }}>
                <CustomButton loading={false} buttonText="Cancel" buttonTextLoading="" handleSubmitClick={() => setShowConfirmDialog(false)} background="transparent" color="rgba(74, 30, 71, 1)" border="1px solid rgba(203, 46, 186, 1)" hoverBgColor="rgba(203, 46, 186, 0.08)" />
                <CustomButton loading={false} buttonText="Confirm Update" buttonTextLoading="" handleSubmitClick={handleConfirmUpdate} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === "detail" && selectedAdr) {
    return (
      <div className="card">
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
          <CustomButton loading={false} buttonText={<><img src="/arrow_right.png" alt="" style={{ width: 14, height: 14, verticalAlign: "middle", marginRight: 4, transform: "scaleX(-1)" }} /> Back to list</>} buttonTextLoading="" handleSubmitClick={() => setView("list")} background="transparent" color="rgba(74, 30, 71, 1)" border="1px solid rgba(203, 46, 186, 1)" hoverBgColor="rgba(203, 46, 186, 0.08)" />
          <CustomButton loading={false} buttonText="Edit" buttonTextLoading="" handleSubmitClick={() => {
            setEditStatus(selectedAdr.status);
            setEditContent(selectedAdr.content ?? "");
            setEditLinkedSymbols((selectedAdr.linked_symbols ?? []).map((s) => s.id).join(", "));
            setEditError(null);
            setView("edit");
          }} />
        </div>
        <h3 style={{ marginTop: 0 }}>{selectedAdr.title}</h3>
        <div className="row" style={{ gap: 8, marginBottom: 16 }}>
          <span className={`pill ${selectedAdr.status === "deprecated" || selectedAdr.status === "superseded" ? "danger" : ""}`}>{selectedAdr.status}</span>
          <span className="muted">{selectedAdr.date}</span>
        </div>
        {loadingDetail
          ? <p className="muted">Loading detail…</p>
          : <div style={{ marginBottom: 16, whiteSpace: "pre-wrap" }}>{selectedAdr.content ?? <span className="muted">No content.</span>}</div>
        }
        {(selectedAdr.linked_symbols ?? []).length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h4>Linked Symbols</h4>
            <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
              {(selectedAdr.linked_symbols ?? []).map((sym) => (
                <button key={sym.id} type="button" className="pill" onClick={() => navigate(`/projects/${projectId}/repos/${repoId}/explore?symbol=${encodeURIComponent(sym.id)}`)} aria-label={`Navigate to ${sym.name}`}>{sym.name}</button>
              ))}
            </div>
          </div>
        )}
        {versionHistory.length > 1 && (
          <div style={{ marginTop: 24 }}>
            <h4>Version History</h4>
            <div style={{ borderLeft: "2px solid var(--border)", paddingLeft: 16 }} aria-label="Version history timeline">
              {versionHistory.map((adr, idx) => (
                <div key={adr.id} style={{ marginBottom: 12, opacity: adr.id === selectedAdr.id ? 1 : 0.7, fontWeight: adr.id === selectedAdr.id ? "bold" : "normal" }}>
                  <span className="muted" style={{ fontSize: 12 }}>{adr.date}</span>
                  <div>
                    {adr.id === selectedAdr.id ? (
                      <span>{adr.title} (current)</span>
                    ) : (
                      <button type="button" onClick={() => setSelectedAdr(adr)} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", padding: 0, textDecoration: "underline" }}>{adr.title}</button>
                    )}
                    <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>{adr.status}</span>
                    {idx < versionHistory.length - 1 && <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>→ superseded</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default: list view
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Architecture Decision Records</h3>
        <CustomButton loading={false} buttonText="+ New ADR" buttonTextLoading="" handleSubmitClick={() => {
          setCreateTitle(""); setCreateStatus("proposed"); setCreateDate(new Date().toISOString().slice(0, 10));
          setCreateContent(""); setCreateLinkedSymbols(""); setCreateError(null); setView("create");
        }} />
      </div>
      {adrs.length === 0 ? (
        <p className="muted">No ADRs found. Create one to get started.</p>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="projects-th">Title</TableCell>
                <TableCell className="projects-th">Status</TableCell>
                <TableCell className="projects-th">Date</TableCell>
                <TableCell className="projects-th">Linked Symbols</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adrs.map((adr) => (
                <TableRow key={adr.id} className="projects-row" onClick={() => openDetail(adr)} style={{ cursor: "pointer" }} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(adr); } }}>
                  <TableCell className="projects-td"><a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDetail(adr); }}>{adr.title}</a></TableCell>
                  <TableCell className="projects-td"><span className={`pill ${adr.status === "deprecated" || adr.status === "superseded" ? "danger" : ""}`}>{adr.status}</span></TableCell>
                  <TableCell className="projects-td" sx={{ color: "rgba(120,120,125,1) !important" }}>{adr.date}</TableCell>
                  <TableCell className="projects-td">{(adr.linked_symbols ?? []).length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
